import styled from "@emotion/styled";
import { ArrowLeftIcon } from "lucide-react";
import { useCallback } from "react";
import { FREE_TEXT_COPY } from "../../../constants/feedback-comments";
import { colors, fonts, radius, spacing } from "../../design-tokens";
import { ParagraphSmall } from "../../primitives/typographies";

const TextAreaContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm}px;
`;

const BackHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs}px;
  color: ${colors.text};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: ${spacing.xs}px;
  color: ${colors.textSecondary};
  transition: color 200ms ease-in-out;

  &:hover {
    color: ${colors.text};
  }
`;

const BackText = styled(ParagraphSmall)`
  color: ${colors.text};
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: ${spacing.md}px;
  border: 1px solid ${colors.textSecondary};
  border-radius: ${radius.sm}px;
  background: transparent;
  color: ${colors.text};
  ${fonts.bodyFont}
  font-size: 14px;
  line-height: 1.4;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
  
  &::placeholder {
    color: ${colors.textSecondary};
    opacity: 1;
  }

  &:focus {
    outline: none;
    border-color: ${colors.text};
  }
`;

const SubmitButton = styled.button`
  align-self: flex-start;
  padding: ${spacing.sm}px ${spacing.md}px;
  background: ${colors.background};
  color: ${colors.text};
  border: 1px solid ${colors.textSecondary};
  border-radius: ${radius.sm}px;
  font-family: inherit;
  ${fonts.bodyFont}
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  cursor: pointer;
  transition: all 200ms ease-in-out;
  text-align: center;
  white-space: nowrap;

  &:hover:not(:disabled) {
    color: ${colors.text};
    background: ${colors.surface};
    border-color: ${colors.text};
  }

  &:active:not(:disabled) {
    background: ${colors.surfaceHover};
    transform: scale(0.98);
  }

  &:disabled {
    color: ${colors.textSecondary};
    border-color: ${colors.border};
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

type FreeTextInputProps = {
  readonly isSubmitting: boolean;
  readonly value: string;
  readonly onChange: (text: string) => void;
  readonly onBack?: () => void;
  readonly onSubmit: (text: string) => void;
  readonly showBack?: boolean;
};

export function FreeTextInput({ isSubmitting, onBack, onChange, onSubmit, showBack = true, value }: FreeTextInputProps) {
  const handleSubmit = useCallback(() => {
    if (!value.trim() || isSubmitting)
      return;
    onSubmit(value.trim());
  }, [isSubmitting, onSubmit, value]);

  return (
    <TextAreaContainer>
      <BackHeader>
        {showBack && (
          <BackButton onClick={onBack} aria-label="Go back to comment options" disabled={isSubmitting}>
            <ArrowLeftIcon size={16} strokeWidth={1.5} />
          </BackButton>
        )}
        <BackText>{FREE_TEXT_COPY}</BackText>
      </BackHeader>
      <TextArea
        placeholder="Add specific details"
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={isSubmitting}
      />
      <SubmitButton
        onClick={handleSubmit}
        disabled={isSubmitting || !value.trim()}
      >
        Submit
      </SubmitButton>
    </TextAreaContainer>
  );
}
